import { InputSelect } from "@r8er/components/input/select";
import { InputShortText } from "@r8er/components/input/short-text";
import { ButtonContainer } from "@r8er/components/marketing/button";
import buttonClasses from "@r8er/components/marketing/button/button.module.css";
import { Column, Columns } from "@r8er/components/marketing/columns";
import columnClasses from "@r8er/components/marketing/columns/columns.module.css";
import { Container } from "@r8er/components/marketing/container";
import { Divider } from "@r8er/components/marketing/divider";
import { Layout } from "@r8er/components/marketing/layout";
import layoutClasses from "@r8er/components/marketing/layout/layout.module.css";
import { Newsletter } from "@r8er/components/marketing/newsletter";
import { ReCAPTCHA } from "@r8er/components/recaptcha";
import { api } from "@r8er/lib/api";
import {
  SECTOR_OPTIONS,
  SECTOR_PROMPT,
  SECTOR_REQUIRED_ERROR,
} from "@r8er/lib/config/sectors";
import type { EnquirySubmission } from "@r8er/lib/resolvers/enquiry/createEnquiry";
import Head from "next/head";
import Link from "next/link";
import { createRef, useState } from "react";
import type ReCAPTCHAV2 from "react-google-recaptcha";

const Watermark = () => (
  <svg
    style={{
      position: "absolute",
      top: 0,
      left: 0,
      transform: "translate(-35%,0) scale(1.25)",
      opacity: "50%",
    }}
    xmlns="http://www.w3.org/2000/svg"
    width="649.6"
    height="1051.7"
  >
    <path
      fill="#f0f0f0"
      d="M516 79a270 270 0 00-382 0 270 270 0 00-23 356l49-48c-56-79-49-190 22-260a203 203 0 01286 0c79 79 79 207 0 286L325 556l-74-73-48 47 122 122 191-191a270 270 0 000-382"
    />
    <path
      fill="#f0f0f0"
      d="M555 497l-15-14-48 47 15 15a258 258 0 010 364 258 258 0 01-364 0 258 258 0 010-364l182-182 74 74 48-48-122-122L95 497a325 325 0 000 460 325 325 0 00460 0 325 325 0 000-460"
    />
  </svg>
);

export default function About() {
  const [recaptchaToken, setRecaptchaToken] = useState<string | undefined>();

  const fieldTypes = {
    type: "text",
    firstName: "text",
    lastName: "text",
    companyName: "text",
    email: "email",
    sector: "text",
    message: "text",
    optin_terms: "checkbox",
    optin_newsletter: "checkbox",
  };

  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState("");

  const recaptchaRef = createRef<ReCAPTCHAV2>();

  const [enquiryData, setEnquiryData] = useState<EnquirySubmission>({
    type: "",
    firstName: "",
    lastName: "",
    companyName: "",
    email: "",
    sector: "",
    message: "",
    optin_terms: false,
    optin_newsletter: false,
    recaptcha_token: "",
  });

  const [errors, setErrors] = useState({
    type: false,
    firstName: false,
    lastName: false,
    companyName: false,
    email: false,
    sector: false,
    message: false,
  });

  const setEnquiryField = (field: string, e) => {
    const currentEnquiryData = { ...enquiryData };

    if (typeof e == "string") {
      currentEnquiryData[field] = e;
      setEnquiryData(currentEnquiryData);
      validateEnquiryField(field, e);
    } else {
      if (fieldTypes[field] !== "checkbox") {
        currentEnquiryData[field] = e.target.value;
      } else {
        currentEnquiryData[field] = e.target.checked;
      }

      setEnquiryData(currentEnquiryData);
      validateEnquiryField(field, e.target.value);
    }
  };

  const validateEnquiryField = (field: string, value: string) => {
    const newErrors = { ...errors };

    switch (fieldTypes[field]) {
      case "text":
      default:
        newErrors[field] = !(value.length > 0);
        break;
      case "email":
        newErrors[field] = !value.match(/.+@.+\..+/);
        break;
      case "checkbox":
        newErrors[field] = field == "optin_terms" && !value;
        break;
    }

    setErrors(newErrors);
    return newErrors[field];
  };

  const successRef = createRef<HTMLDivElement>();

  const validateAll = () => {
    const validationResults = { ...errors };

    for (const [k, v] of Object.entries(enquiryData)) {
      if (k !== "recaptcha_token") {
        validationResults[k] = validateEnquiryField(k, v);
      }
    }

    setErrors(validationResults);
    return Object.values(validationResults).filter((v) => v).length == 0;
  };

  const submitForm = async () => {
    const scrollDiv = successRef?.current;

    if (!validateAll()) {
      scrollDiv?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    setFormSubmitting(true);

    if (recaptchaRef.current) {
      enquiryData.recaptcha_token = recaptchaRef.current.getValue();
    }

    setSubmissionError("");

    try {
      const submitted = await api.enquiry.create.mutate({
        enquiry: enquiryData,
      });

      if (submitted === false) {
        setSubmissionError(
          "We couldn't submit your enquiry at this time. Please try again.",
        );
        setFormSubmitting(false);
        return;
      }
    } catch {
      setSubmissionError(
        "We couldn't submit your enquiry at this time. Please try again.",
      );
      setFormSubmitting(false);
      return;
    }

    scrollDiv?.scrollIntoView({ behavior: "smooth" });

    setFormSubmitted(true);
  };

  return (
    <div ref={successRef}>
      <Head>
        <title>Contact Us - R8ER</title>
        <link rel="icon" href="/favicon.png" />
      </Head>

      <Layout>
        <div
          style={{
            position: "relative",
            overflow: "hidden",
            paddingBottom: "5rem",
          }}
        >
          <Watermark />

          <Container width={1100}>
            <h1
              style={{
                marginTop: "5rem",
                marginBottom: "-4rem",
                color: "var(--brand-secondary)",
                textAlign: "center",
                fontSize: "2.625rem",
                fontWeight: "normal",
              }}
            >
              Contact Us
            </h1>
            <Divider />
          </Container>

          <Container width={1100}>
            {!formSubmitted && (
              <div>
                <form>
                  <Columns count={2}>
                    <Column style={{ overflow: "visible" }}>
                      <InputSelect
                        id="type"
                        name="type"
                        placeholder="Enquiry type"
                        options={[
                          {
                            value: "entrepreneur",
                            label: "I am an entrepreneur...",
                          },
                          {
                            value: "expert",
                            label: "I am an expert...",
                          },
                          { value: "investor", label: "I am an investor..." },
                          { value: "partner", label: "I am a partner..." },
                          { value: "other", label: "Other" },
                        ]}
                        theme={{
                          selectedOptionContainer:
                            layoutClasses.marketing__layout_input_selectedOptionsContainer,
                          selectContainer:
                            layoutClasses.marketing__layout_input_selectContainer,
                          selectButton:
                            layoutClasses.marketing__layout_input_selectButtonLarge,
                          activeButton:
                            layoutClasses.marketing__layout_input_activeButton,
                          selectOptions:
                            layoutClasses.marketing__layout_input_selectOptions,
                          selectOption:
                            layoutClasses.marketing__layout_input_selectOption,
                        }}
                        onChange={({ newValue }) => {
                          setEnquiryField(
                            "type",
                            newValue as unknown as string,
                          );
                        }}
                      />

                      <InputShortText
                        id="firstName"
                        name="firstName"
                        className={[
                          layoutClasses.marketing__layout_input_compact,
                          errors["firstName"] ? layoutClasses.input_error : "",
                        ].join(" ")}
                        placeholder="First Name"
                        onChange={({ newValue }) =>
                          setEnquiryField("firstName", newValue)
                        }
                      />
                      <InputShortText
                        id="lastName"
                        name="lastName"
                        className={[
                          layoutClasses.marketing__layout_input_compact,
                          errors["lastName"] ? layoutClasses.input_error : "",
                        ].join(" ")}
                        placeholder="Surname"
                        onChange={({ newValue }) =>
                          setEnquiryField("lastName", newValue)
                        }
                      />
                    </Column>

                    <Column style={{ overflow: "visible" }}>
                      <InputShortText
                        id="companyName"
                        name="companyName"
                        className={[
                          layoutClasses.marketing__layout_input_compact,
                          errors["companyName"]
                            ? layoutClasses.input_error
                            : "",
                        ].join(" ")}
                        placeholder="Company name"
                        onChange={({ newValue }) =>
                          setEnquiryField("companyName", newValue)
                        }
                      />
                      <InputShortText
                        id="email"
                        name="email"
                        inputMode="email"
                        className={[
                          layoutClasses.marketing__layout_input_compact,
                          errors["email"] ? layoutClasses.input_error : "",
                        ].join(" ")}
                        placeholder="Email"
                        onChange={({ newValue }) =>
                          setEnquiryField("email", newValue)
                        }
                      />

                      <InputSelect
                        id="sector"
                        name="sector"
                        placeholder={SECTOR_PROMPT}
                        aria-describedby={
                          errors["sector"] ? "sector-error" : undefined
                        }
                        aria-invalid={errors["sector"]}
                        required
                        options={SECTOR_OPTIONS.map(({ value, label }) => ({
                          value,
                          label,
                        }))}
                        theme={{
                          selectedOptionContainer:
                            layoutClasses.marketing__layout_input_selectedOptionsContainer,
                          selectContainer:
                            layoutClasses.marketing__layout_input_selectContainer,
                          selectButton: [
                            layoutClasses.marketing__layout_input_selectButtonLarge,
                            errors["sector"] ? layoutClasses.input_error : "",
                          ].join(" "),
                          activeButton:
                            layoutClasses.marketing__layout_input_activeButton,
                          selectOptions:
                            layoutClasses.marketing__layout_input_selectOptions,
                          selectOption:
                            layoutClasses.marketing__layout_input_selectOption,
                        }}
                        onChange={({ newValue }) => {
                          setEnquiryField(
                            "sector",
                            newValue as unknown as string,
                          );
                        }}
                      />
                    </Column>
                  </Columns>

                  <textarea
                    className={`${
                      errors["message"] ? layoutClasses.input_error : ""
                    }`}
                    rows={4}
                    placeholder="Message"
                    onChange={(e) => setEnquiryField("message", e)}
                  ></textarea>

                  <Columns count={2} style={{ textAlign: "center" }}>
                    <Column>
                      <div className={layoutClasses.checkbox_label}>
                        <input
                          type="checkbox"
                          onChange={(e) => setEnquiryField("optin_terms", e)}
                        />{" "}
                        I accept R8ERs&nbsp;
                        <span className="a">
                          <Link href="/terms">Terms &amp; Conditions</Link>
                        </span>
                      </div>
                    </Column>
                    <Column>
                      <div className={layoutClasses.checkbox_label}>
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            setEnquiryField("optin_newsletter", e)
                          }
                        />{" "}
                        I would like to subscribe to the R8ER Newsletter
                      </div>
                    </Column>
                  </Columns>
                </form>

                <ButtonContainer>
                  {submissionError && (
                    <div className={layoutClasses.text__red} role="alert">
                      {submissionError}
                    </div>
                  )}
                  {errors["sector"] && (
                    <div id="sector-error" className={layoutClasses.text__red}>
                      {SECTOR_REQUIRED_ERROR}
                    </div>
                  )}
                  {errors["optin_terms"] && (
                    <div className={layoutClasses.text__red}>
                      Please accept the terms and conditions to submit your
                      enquiry.
                    </div>
                  )}
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    recaptchaToken={recaptchaToken}
                    type="v2"
                  />
                  <button
                    disabled={formSubmitting}
                    className={[
                      buttonClasses.button_blue,
                      buttonClasses.button_wide,
                    ].join(" ")}
                    onClick={() => submitForm()}
                  >
                    {!formSubmitting ? "Get Started" : "Sending..."}
                  </button>
                </ButtonContainer>
              </div>
            )}

            {formSubmitted && (
              <div className={layoutClasses.marketing__notice}>
                {enquiryData.sector === "climate"
                  ? "Thanks for your interest in R8ER! We have noted your contact information and will be in touch once we are live in the climate sector."
                  : "Thank you for your enquiry. A member of the R8ER team will respond as soon as possible."}
              </div>
            )}
          </Container>
        </div>

        <Columns count={2} style={{ minHeight: "950px", gridColumnGap: 0 }}>
          <Column
            classList={[columnClasses.marketing__columns_inner_overlay]}
            style={{ backgroundImage: 'url("./contact-address.png")' }}
          >
            <div className={columnClasses.marketing__columns_inner_center}>
              <h2>Address</h2>
              <p>
                167-169 Great Portland Street,
                <br />
                5th Floor,
                <br />
                London, W1W 5PF
                <br />
                United Kingdom
              </p>
              <h2>Email</h2>
              <p>
                <a href="mailto:info@r8er.co.uk">info@r8er.co.uk</a>
              </p>
            </div>
          </Column>
          <Column
            style={{
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundImage: "url(/contact-map.png)",
            }}
          ></Column>
        </Columns>

        <Newsletter />
      </Layout>
    </div>
  );
}
